/**
 * Design system component: ItemDetailPanel
 *
 * Full-screen detail view for a single item, rendered below the sticky header
 * when the user taps an item in the catalogue or My Items list.
 *
 * Sections rendered (in order):
 *   1. 16:9 hero panel — photo or emoji fallback with category background
 *   2. Status pill + PolicyBadge — always visible, never hidden behind a tap
 *   3. Description prose block (if set)
 *   4. Item details panel — category, brand, condition
 *   5. Category-specific detail panel — loaded async from the 1:1 detail table
 *      (board_game_details, book_details, puzzle_details, toy_details,
 *       event_party_details); not shown for TOOL which has no detail table
 *   6. PossessionChain — owner → borrower custody chain
 *   7. Primary CTA — variant determined by viewer role:
 *        `owner`   → non-interactive "This is your item" label
 *        `return`  → "Mark as returned" (current borrower)
 *        `request` → "Request to borrow" (other group member who can request)
 *        `disabled`→ "Currently on loan" (no action available)
 *   8. Owner actions — Edit, Lend, Add/Remove from Group, Delete, Loan History
 *      (only visible to the item owner)
 *
 * The panel also contains two Dialogs (request loan, remove from group picker)
 * and an AlertDialog (remove from group confirmation) managed inline.
 *
 * Loan request approver logic mirrors `useLoanRequests.ts`:
 *   - FREE_CIRCULATION: request goes to current holder (borrower_id)
 *   - RETURN_TO_OWNER_REQUIRED: request goes to owner
 */
import { useState, useEffect } from 'react';
import { T, CAT_FALLBACK, CATEGORY_EMOJI, CATEGORY_LABEL, RADIUS, SHADOW } from '@/lib/tokens';
import { PolicyBadge } from './PolicyBadge';
import {
  loadCategoryDetails,
  CategoryDetailsState,
  BOARD_GAME_TYPE_OPTIONS,
  PUZZLE_TYPE_OPTIONS,
  PUZZLE_THEME_OPTIONS,
  BOOK_FORMAT_OPTIONS,
  BOOK_GENRE_OPTIONS,
  TOY_TYPE_OPTIONS,
  EVENT_PARTY_TYPE_OPTIONS,
  TRANSPORT_SIZE_OPTIONS,
  POWER_TYPE_OPTIONS,
} from '@/lib/itemCategoryDetails';
import { PossessionChain } from './PossessionChain';
import { CTAButton } from './CTAButton';
import { LoanHistoryDialog } from '@/components/LoanHistoryDialog';
import { RequestLoanDialog } from '@/components/RequestLoanDialog';
import { RemoveFromGroupDialog } from '@/components/RemoveFromGroupDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { Pencil, Trash2, HandCoins, FolderPlus, FolderMinus, CalendarClock, Bell, Gift } from 'lucide-react';
import { DueDateBadge } from './DueDateBadge';
import { useLoanNudge } from '@/hooks/useLoanNudge';
import { SetDueDateDialog } from '@/components/SetDueDateDialog';
import type { ItemWithOwner } from '@/hooks/useItems';
import { canViewBorrowerDetails } from '@/lib/loanVisibility';
import { Switch } from '@/components/ui/switch';
import { useToggleGivingAway } from '@/hooks/useToggleGivingAway';

interface ItemGroup {
  id: string;
  name: string;
}

interface ItemDetailPanelProps {
  item: ItemWithOwner;
  currentUserId: string;
  onBack: () => void;
  onInitiateReturn: (item: ItemWithOwner) => void;
  onConfirmReturn: (item: ItemWithOwner) => void;
  onCancelReturn: (item: ItemWithOwner) => void;
  onLend: (item: ItemWithOwner) => void;
  onEdit: (item: ItemWithOwner) => void;
  onDelete: (item: ItemWithOwner) => void;
  /** Accept a pending loan proposal (borrower only). */
  onAcceptProposal: (item: ItemWithOwner) => void;
  /** Cancel/decline a pending loan proposal (borrower or owner). */
  onCancelProposal: (item: ItemWithOwner) => void;
  /** Current group context (for Remove from Group pre-selection). */
  groupId?: string;
  /** Groups the item belongs to (enables Remove from Group). */
  itemGroups?: ItemGroup[];
  /** Callback to trigger Add to Group flow (My Items context). */
  onAddToGroup?: () => void;
}

/**
 *
 * @param root0
 * @param root0.item
 * @param root0.currentUserId
 * @param root0.onBack
 * @param root0.onInitiateReturn
 * @param root0.onConfirmReturn
 * @param root0.onCancelReturn
 * @param root0.onLend
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.onAcceptProposal
 * @param root0.onCancelProposal
 * @param root0.groupId
 * @param root0.itemGroups
 * @param root0.onAddToGroup
 */
export function ItemDetailPanel({
  item,
  currentUserId,
  onBack,
  onInitiateReturn,
  onConfirmReturn,
  onCancelReturn,
  onLend,
  onEdit,
  onDelete,
  onAcceptProposal,
  onCancelProposal,
  groupId,
  itemGroups,
  onAddToGroup,
}: ItemDetailPanelProps) {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [removeGroupOpen, setRemoveGroupOpen] = useState(false);
  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const { user } = useAuth();
  const { isOnline } = useNetworkStatusContext();
  const loanNudge = useLoanNudge();
  const toggleGivingAway = useToggleGivingAway();

  const catColours = item.category && T.cat[item.category] ? T.cat[item.category] : CAT_FALLBACK;
  const emoji = item.category ? (CATEGORY_EMOJI[item.category] ?? '📦') : '📦';
  const categoryLabel = item.category ? (CATEGORY_LABEL[item.category] ?? item.category) : '';
  const { url: photoSignedUrl } = useSignedUrl(item.photo_url);

  const [catDetails, setCatDetails] = useState<CategoryDetailsState | null>(null);

  useEffect(() => {
    if (item.category && item.category !== 'TOOL') {
      loadCategoryDetails(item.id, item.category).then(setCatDetails);
    } else {
      setCatDetails(null);
    }
  }, [item.id, item.category]);

  const labelOf = (options: { value: string; label: string }[], val: string) =>
    options.find((o) => o.value === val)?.label ?? val;

  const isOwner = item.owner_id === currentUserId;
  const borrowerIsCurrentUser = item.active_loan?.borrower_id === currentUserId;
  const isAvailable = !item.active_loan;
  const isOverdue = !!item.active_loan?.due_at && new Date(item.active_loan.due_at) < new Date();
  const hasActiveLoan = !!item.active_loan;
  const isReturnPending = item.active_loan?.status === 'RETURN_PENDING';
  const isPendingAcceptance = item.active_loan?.status === 'PENDING_ACCEPTANCE';
  const returnInitiatedByCurrentUser = item.active_loan?.return_initiated_by === currentUserId;
  const loanMasked = !canViewBorrowerDetails(item.active_loan, groupId);
  const userIsConfirmer =
    isReturnPending && !returnInitiatedByCurrentUser && (borrowerIsCurrentUser || isOwner);

  const availableGroups = itemGroups ?? (groupId ? [{ id: groupId, name: '' }] : []);
  const canShowRemoveFromGroup = isOwner && availableGroups.length > 0;

  const getApproverId = (): string | null => {
    if (item.active_loan && item.circulation_mode === 'FREE_CIRCULATION_ALLOWED') {
      return item.active_loan.borrower_id;
    }
    if (!item.active_loan) return item.owner_id;
    return null;
  };
  const approverId = getApproverId();
  const isCurrentHolder = item.active_loan?.borrower_id === user?.id;
  const canRequest = !isOwner && !isCurrentHolder && !!approverId && approverId !== user?.id;

  const statusPill = (() => {
    if (isAvailable)
      return { bg: T.successSoft, text: T.success, border: T.successBorder, label: 'Available' };
    if (isPendingAcceptance)
      return {
        bg: T.accentSoft,
        text: T.accent,
        border: T.accentBorder,
        label: 'Awaiting acceptance',
      };
    if (isReturnPending)
      return {
        bg: T.warningSoft,
        text: T.warning,
        border: T.warningBorder,
        label: 'Return pending',
      };
    if (borrowerIsCurrentUser)
      return {
        bg: T.warningSoft,
        text: T.warning,
        border: T.warningBorder,
        label: isOverdue ? 'Overdue' : 'With you',
      };
    return { bg: T.warningSoft, text: T.warning, border: T.warningBorder, label: 'On loan' };
  })();

  const ctaVariant = (() => {
    if (isPendingAcceptance && borrowerIsCurrentUser) return 'accept-proposal';
    if (isPendingAcceptance && isOwner) return 'proposal-pending';
    if (isOwner && isAvailable) return 'owner';
    if (isReturnPending && userIsConfirmer) return 'confirm-return';
    if (isReturnPending && returnInitiatedByCurrentUser) return 'return-pending';
    if (isReturnPending) return 'return-pending'; // Neither party but return is pending
    if (borrowerIsCurrentUser) return 'return';
    if (isOwner && hasActiveLoan) return 'return'; // Owner can also initiate return
    if (canRequest) return 'request';
    return 'disabled';
  })();

  const getApproverLabel = () => {
    if (item.active_loan && item.circulation_mode === 'FREE_CIRCULATION_ALLOWED') {
      return item.active_loan.borrower_name || 'Current Holder';
    }
    return item.owner_name || 'Owner';
  };

  return (
    <div>
      {/* Hero panel — 4:3, photo fills box; emoji centred as fallback */}
      <div
        style={{
          marginTop: 16,
          aspectRatio: '4/3',
          background: photoSignedUrl ? T.surfaceSubtle : catColours.bg,
          borderRadius: RADIUS.xl,
          border: `1px solid ${T.border}`,
          boxShadow: SHADOW.md,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {photoSignedUrl ? (
          <img
            src={photoSignedUrl}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{ fontSize: 64 }}>{emoji}</span>
        )}
      </div>

      {/* Status pill + policy badge — directly below hero */}
      <div
        style={{ paddingTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
      >
        <span
          style={{
            flexShrink: 0,
            padding: '4px 10px',
            borderRadius: RADIUS.xl,
            fontSize: 12,
            fontWeight: 600,
            background: statusPill.bg,
            color: statusPill.text,
            border: `1px solid ${statusPill.border}`,
          }}
        >
          {statusPill.label}
        </span>
        <PolicyBadge policy={item.circulation_mode} />
      </div>

      {/* Description — prose block per §6.3, outside the detail panel */}
      {item.description && (
        <p
          style={{
            margin: '16px 0 0',
            fontSize: 14,
            fontWeight: 400,
            color: T.textSec,
            lineHeight: 1.65,
          }}
        >
          {item.description}
        </p>
      )}

      {/* Giving-away note — visible to all group members when tag is active */}
      {item.is_giving_away && (
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 13,
            color: T.textSec,
            lineHeight: 1.55,
          }}
        >
          The owner is giving this away — get in touch if you'd like it.
        </p>
      )}

      {/* Detail list */}
      <div
        style={{
          marginTop: 16,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.lg,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '9px 14px',
            background: T.surfaceSubtle,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: T.textMuted,
            textTransform: 'uppercase',
          }}
        >
          Item details
        </div>
        {categoryLabel && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '11px 14px',
              borderBottom: item.brand ? `1px solid ${T.border}` : undefined,
            }}
          >
            <span style={{ fontSize: 13, color: T.text }}>Category</span>
            <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{categoryLabel}</span>
          </div>
        )}
        {item.brand && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '11px 14px',
            }}
          >
            <span style={{ fontSize: 13, color: T.text }}>Brand</span>
            <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{item.brand}</span>
          </div>
        )}
      </div>

      {/* Category-specific details */}
      {catDetails &&
        item.category &&
        item.category !== 'TOOL' &&
        (() => {
          const rows: { label: string; value: string }[] = [];
          if (item.category === 'BOARD_GAME') {
            const d = catDetails.boardGame;
            if (d.gameType)
              rows.push({
                label: 'Game Type',
                value: labelOf(BOARD_GAME_TYPE_OPTIONS, d.gameType),
              });
            if (d.minPlayers && d.maxPlayers)
              rows.push({ label: 'Players', value: `${d.minPlayers} – ${d.maxPlayers}` });
            if (d.playTimeMinutes)
              rows.push({ label: 'Play Time', value: `${d.playTimeMinutes} min` });
            if (d.recommendedAge)
              rows.push({ label: 'Recommended Age', value: `${d.recommendedAge}+` });
            if (d.publisher) rows.push({ label: 'Publisher', value: d.publisher });
            if (d.missingComponents)
              rows.push({ label: 'Missing Components', value: d.missingComponentsNotes || 'Yes' });
          } else if (item.category === 'PUZZLE') {
            const d = catDetails.puzzle;
            if (d.puzzleType)
              rows.push({
                label: 'Puzzle Type',
                value: labelOf(PUZZLE_TYPE_OPTIONS, d.puzzleType),
              });
            if (d.theme)
              rows.push({ label: 'Theme', value: labelOf(PUZZLE_THEME_OPTIONS, d.theme) });
            if (d.pieceCount) rows.push({ label: 'Pieces', value: d.pieceCount });
            if (d.recommendedAge)
              rows.push({ label: 'Recommended Age', value: `${d.recommendedAge}+` });
            rows.push({ label: 'Complete', value: d.complete ? 'Yes' : 'No' });
            if (d.hasOriginalBox) rows.push({ label: 'Has Original Box', value: 'Yes' });
          } else if (item.category === 'BOOK') {
            const d = catDetails.book;
            if (d.author) rows.push({ label: 'Author', value: d.author });
            if (d.format)
              rows.push({ label: 'Format', value: labelOf(BOOK_FORMAT_OPTIONS, d.format) });
            if (d.genre) rows.push({ label: 'Genre', value: labelOf(BOOK_GENRE_OPTIONS, d.genre) });
            if (d.language) rows.push({ label: 'Language', value: d.language });
            if (d.seriesName)
              rows.push({
                label: 'Series',
                value: d.seriesNumber ? `${d.seriesName} #${d.seriesNumber}` : d.seriesName,
              });
            if (d.recommendedAge)
              rows.push({ label: 'Recommended Age', value: `${d.recommendedAge}+` });
            if (d.isbn13) rows.push({ label: 'ISBN-13', value: d.isbn13 });
          } else if (item.category === 'TOY') {
            const d = catDetails.toy;
            if (d.toyType)
              rows.push({ label: 'Toy Type', value: labelOf(TOY_TYPE_OPTIONS, d.toyType) });
            if (d.recommendedAge)
              rows.push({ label: 'Recommended Age', value: `${d.recommendedAge}+` });
            if (d.containsSmallParts) rows.push({ label: 'Contains Small Parts', value: 'Yes' });
            rows.push({ label: 'Battery Required', value: d.batteryRequired ? 'Yes' : 'No' });
          } else if (item.category === 'EVENT_PARTY') {
            const d = catDetails.eventParty;
            if (d.eventType)
              rows.push({
                label: 'Event Type',
                value: labelOf(EVENT_PARTY_TYPE_OPTIONS, d.eventType),
              });
            if (d.transportSize)
              rows.push({
                label: 'Transport Size',
                value: labelOf(TRANSPORT_SIZE_OPTIONS, d.transportSize),
              });
            rows.push({ label: 'Power Required', value: d.powerRequired ? 'Yes' : 'No' });
            if (d.powerRequired && d.powerType)
              rows.push({ label: 'Power Type', value: labelOf(POWER_TYPE_OPTIONS, d.powerType) });
            if (d.accessoriesIncluded)
              rows.push({ label: 'Accessories', value: d.accessoriesIncluded });
          }
          if (rows.length === 0) return null;
          return (
            <div
              style={{
                marginTop: 12,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '9px 14px',
                  background: T.surfaceSubtle,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: T.textMuted,
                  textTransform: 'uppercase',
                }}
              >
                {CATEGORY_LABEL[item.category] ?? item.category} details
              </div>
              {rows.map((row, i) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '11px 14px',
                    borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : undefined,
                  }}
                >
                  <span style={{ fontSize: 13, color: T.text }}>{row.label}</span>
                  <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{row.value}</span>
                </div>
              ))}
            </div>
          );
        })()}

      {/* Possession chain */}
      <div style={{ padding: '16px 0 20px' }}>
        <PossessionChain
          ownerName={item.owner_name ?? 'Unknown'}
          ownerUserId={item.owner_id}
          borrowerName={item.active_loan?.borrower_name}
          borrowerUserId={item.active_loan?.borrower_id}
          dueAt={item.active_loan?.due_at}
          category={item.category}
          currentUserId={currentUserId}
          masked={loanMasked}
        />
        {item.active_loan?.due_at && (
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
            <DueDateBadge dueAt={item.active_loan.due_at} />
          </div>
        )}
      </div>

      {/* Loan note (visible only to the borrower) */}
      {borrowerIsCurrentUser && item.active_loan?.notes && (
        <div style={{ padding: '0 0 16px' }}>
          <div style={{ height: 1, background: T.border, marginBottom: 12 }} />
          <div style={{ fontSize: 12, fontWeight: 400, color: T.textMuted, marginBottom: 4, letterSpacing: '0.005em' }}>
            Note from {item.owner_name?.split(' ')[0] ?? 'Owner'}
          </div>
          <div style={{ fontSize: 14, fontWeight: 400, color: T.textSec, lineHeight: 1.65 }}>
            {item.active_loan.notes}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ paddingBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ctaVariant === 'accept-proposal' ? (
          <>
            <CTAButton
              variant={isOnline ? 'return' : 'disabled'}
              onClick={isOnline ? () => onAcceptProposal(item) : undefined}
            >
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <HandCoins size={15} /> Accept loan
              </span>
            </CTAButton>
            <CTAButton
              variant={isOnline ? 'secondary' : 'disabled'}
              onClick={isOnline ? () => onCancelProposal(item) : undefined}
            >
              Decline
            </CTAButton>
          </>
        ) : ctaVariant === 'proposal-pending' ? (
          <>
            <CTAButton variant="info">Loan proposal sent — awaiting acceptance</CTAButton>
            <CTAButton
              variant={isOnline ? 'secondary' : 'disabled'}
              onClick={isOnline ? () => onCancelProposal(item) : undefined}
            >
              Cancel proposal
            </CTAButton>
          </>
        ) : ctaVariant === 'confirm-return' ? (
          <CTAButton
            variant={isOnline ? 'return' : 'disabled'}
            onClick={isOnline ? () => onConfirmReturn(item) : undefined}
          >
            Confirm return
          </CTAButton>
        ) : ctaVariant === 'return-pending' ? (
          <CTAButton variant="info">Return pending — awaiting confirmation</CTAButton>
        ) : (
          <CTAButton
            variant={
              !isOnline && (ctaVariant === 'return' || ctaVariant === 'request')
                ? 'disabled'
                : ctaVariant === 'return'
                  ? 'return'
                  : ctaVariant === 'request'
                    ? 'request'
                    : ctaVariant === 'owner'
                      ? 'owner'
                      : 'disabled'
            }
            onClick={
              ctaVariant === 'return' && isOnline
                ? () => onInitiateReturn(item)
                : ctaVariant === 'request' && isOnline
                  ? () => setRequestDialogOpen(true)
                  : undefined
            }
          >
            {ctaVariant === 'request'
              ? 'Request to borrow'
              : ctaVariant === 'return'
                ? 'Mark as returned'
                : ctaVariant === 'owner'
                  ? 'This is your item'
                  : 'Currently on loan'}
          </CTAButton>
        )}

        {/* Cancel return button — shown when return is pending and user is borrower or owner */}
        {isReturnPending && (borrowerIsCurrentUser || isOwner) && (
          <CTAButton
            variant={isOnline ? 'secondary' : 'disabled'}
            onClick={isOnline ? () => onCancelReturn(item) : undefined}
          >
            Cancel return
          </CTAButton>
        )}

        {/* Owner force-complete — shown when the owner initiated the return */}
        {isReturnPending && isOwner && returnInitiatedByCurrentUser && (
          <CTAButton
            variant={isOnline ? 'return' : 'disabled'}
            onClick={isOnline ? () => onConfirmReturn(item) : undefined}
          >
            Complete return
          </CTAButton>
        )}

        {/* Holder lend — free-circulation holders can pass the item to another member */}
        {borrowerIsCurrentUser &&
          item.circulation_mode === 'FREE_CIRCULATION_ALLOWED' &&
          !isPendingAcceptance &&
          !isReturnPending && (
            <CTAButton
              variant={isOnline ? 'request' : 'disabled'}
              onClick={isOnline ? () => onLend(item) : undefined}
            >
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <HandCoins size={15} /> Lend to Member
              </span>
            </CTAButton>
          )}
      </div>

      {/* Owner actions — full-width buttons */}
      {isOwner && (
        <div style={{ paddingBottom: 40, display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Giving-away toggle — only shown when item is AVAILABLE */}
          {item.status === 'AVAILABLE' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderRadius: RADIUS.sm,
                border: `1px solid ${T.border}`,
                background: T.surfaceSubtle,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Gift size={15} color={T.textSec} />
                <span style={{ fontSize: 14, color: T.text }}>Giving this away?</span>
              </div>
              <Switch
                checked={item.is_giving_away ?? false}
                onCheckedChange={(checked) =>
                  toggleGivingAway.mutate({ itemId: item.id, isGivingAway: checked })
                }
                disabled={toggleGivingAway.isPending}
              />
            </div>
          )}

          <CTAButton variant="secondary" onClick={() => onEdit(item)}>
            <span
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Pencil size={15} /> Edit Item
            </span>
          </CTAButton>

          {hasActiveLoan && (
            <CTAButton variant="secondary" onClick={() => setDueDateDialogOpen(true)}>
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <CalendarClock size={15} />{' '}
                {item.active_loan?.due_at ? 'Update Due Date' : 'Set Due Date'}
              </span>
            </CTAButton>
          )}

          {/* Send reminder — owner can nudge the borrower on an active loan */}
          {hasActiveLoan &&
            !borrowerIsCurrentUser &&
            item.active_loan?.status === 'ACTIVE' && (
              <CTAButton
                variant="secondary"
                onClick={() =>
                  loanNudge.mutate({
                    loanId: item.active_loan!.id,
                    itemId: item.id,
                  })
                }
                disabled={loanNudge.isPending}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <Bell size={15} /> {loanNudge.isPending ? 'Sending...' : 'Send Reminder'}
                </span>
              </CTAButton>
            )}

          {!hasActiveLoan && (
            <CTAButton variant="request" onClick={() => onLend(item)}>
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <HandCoins size={15} /> Lend to Member
              </span>
            </CTAButton>
          )}

          {onAddToGroup && (
            <CTAButton variant="secondary" onClick={onAddToGroup}>
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <FolderPlus size={15} /> Add to Group
              </span>
            </CTAButton>
          )}

          {canShowRemoveFromGroup &&
            (hasActiveLoan ? (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 13,
                  color: T.textMuted,
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                Item is currently on loan — return it before removing from this group
              </p>
            ) : (
              <CTAButton variant="danger" onClick={() => setRemoveGroupOpen(true)}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <FolderMinus size={15} /> Remove from Group
                </span>
              </CTAButton>
            ))}

          {!hasActiveLoan && (
            <CTAButton variant="danger" onClick={() => onDelete(item)}>
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <Trash2 size={15} /> Delete Item
              </span>
            </CTAButton>
          )}

          <LoanHistoryDialog itemId={item.id} itemName={item.name} isOwner={isOwner} />
        </div>
      )}

      {/* Request loan dialog (extracted component) */}
      {approverId && (
        <RequestLoanDialog
          open={requestDialogOpen}
          onOpenChange={setRequestDialogOpen}
          itemId={item.id}
          itemName={item.name}
          approverId={approverId}
          approverLabel={getApproverLabel()}
          groupId={groupId}
        />
      )}

      {/* Set due date dialog (owner only, active loan) */}
      {item.active_loan && (
        <SetDueDateDialog
          open={dueDateDialogOpen}
          onOpenChange={setDueDateDialogOpen}
          loanId={item.active_loan.id}
          itemName={item.name}
          currentDueAt={item.active_loan.due_at}
        />
      )}

      {/* Remove from Group dialog (extracted component) */}
      <RemoveFromGroupDialog
        open={removeGroupOpen}
        onOpenChange={setRemoveGroupOpen}
        itemId={item.id}
        itemName={item.name}
        groups={availableGroups}
        defaultGroupId={groupId}
        onRemoved={onBack}
      />
    </div>
  );
}
